import reportjoborderreversalService from '../../../services/reportjoborderreversalService';
import filterService from '../../../services/filterService';

import {
    setReportjoborderreversalLoading,
    setReportjoborderreversalError,
    setReportjoborderreversalList,
    setReportjoborderreversalCurrent,
    setReportjoborderreversalPagination,
    setReportjoborderreversalSort,
    setReportjoborderreversalFilters,
    setReportjoborderreversalDefaultFilter,
    setReportjoborderreversalSettings,
    clearReportjoborderreversalCurrent,
    clearReportjoborderreversalError,
    setReportjoborderreversalFilterLoading,
    changeReportjoborderreversalFilterCondition,
    addReportjoborderreversalFilterCondition,
    updateReportjoborderreversalFilterConditionRule,
    updateReportjoborderreversalFilterConditionValue,
    removeReportjoborderreversalFilterCondition,
    setReportjoborderreversalSelected,
    setReportjoborderreversalDownloading,
    resetReportjoborderreversalFiltersAndPagination,

    setReportjoborderreversalClassicFilters
} from './reportjoborderreversalSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportjoborderreversal.isSettingsInitialized) {
            dispatch(setReportjoborderreversalLoading(true));
            const response = await reportjoborderreversalService.getSettings();

            // Set the settings
            dispatch(setReportjoborderreversalSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportjoborderreversalClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportjoborderreversalDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportjoborderreversalError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportjoborderreversalLoading(false));
    }
};

export const fetchReportjoborderreversals = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportjoborderreversal;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportjoborderreversalLoading(true));
        const response = await reportjoborderreversalService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportjoborderreversalList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportjoborderreversals:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjoborderreversalError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderreversalLoading(false));
    }
};

export const fetchReportjoborderreversal = (id) => async (dispatch) => {
    try {
        dispatch(setReportjoborderreversalLoading(true));
        const response = await reportjoborderreversalService.getOne(id);
        dispatch(setReportjoborderreversalCurrent(response.data));
    } catch (error) {
        dispatch(setReportjoborderreversalError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportjoborderreversalLoading(false));
    }
};

export const createReportjoborderreversal = (data) => async (dispatch) => {
    try {
        const response = await reportjoborderreversalService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportjoborderreversal = (id, data) => async (dispatch) => {
    try {
        const response = await reportjoborderreversalService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportjoborderreversal = (id) => async (dispatch) => {
    try {
        dispatch(setReportjoborderreversalLoading(true));
        const response = await reportjoborderreversalService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportjoborderreversalError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderreversalLoading(false));
    }
};

export const downloadReportjoborderreversal = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportjoborderreversal;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportjoborderreversalDownloading(true));
        const response = await reportjoborderreversalService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportjoborderreversalDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportjoborderreversals:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjoborderreversalError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjoborderreversalDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportjoborderreversalPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportjoborderreversalSort(sort));
};

export const searchReportjoborderreversals = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportjoborderreversal.filters;
    dispatch(setReportjoborderreversalFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportjoborderreversalDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportjoborderreversalFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportjoborderreversalFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportjoborderreversalFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportjoborderreversalFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportjoborderreversalFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportjoborderreversalFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportjoborderreversal.settings;
            dispatch(setReportjoborderreversalSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportjoborderreversalFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportjoborderreversalFilterLoading(false));
        dispatch(setReportjoborderreversalError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportjoborderreversalSelected(selectedRows));
    };
};

export const clearCurrentReportjoborderreversal = () => {
    return (dispatch) => {
        dispatch(clearReportjoborderreversalCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportjoborderreversalFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportjoborderreversal.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportjoborderreversalClassicFilters(updatedClassicFilters));
};
